import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Moment from 'moment';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { get } from 'lodash';

import MultiGauge from '../../components/charts/multigauge';
import HealthPanel from '../../components/healthpanel';
import HealthService, { IsVM } from '../../data/health-service';
import Auth, { ProfileContext } from '../../data/auth-service';
import { Bytes } from '../../data/format';
import DataContext from '../../data/data-context';

const useStyles = makeStyles(theme => ({
    panel: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
    },
    hlist: {
        display: 'inline-block'
    },
    healthGrid: {
        flexGrow: 1,
    },
    key: {
        ...theme.typography.subtitle1,
    },
    value: {
        ...theme.typography.subtitle2,
        color: theme.palette.info.light
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    errorPanel: {
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.error.main}`,
        marginBottom: theme.spacing(2),
    }
}));

const CMCHealth = () => {
    const classes = useStyles();
    const theme = useTheme();
    const params = useParams();

    const { updateProfile, getProfile, /*perm*/ } = useContext(ProfileContext);
    const [health, setHealth] = useState([]);
    const [history, setHistory] = useState({ history: [] });
    const [historyMetrics, setHistoryMetrics] = useState([]);
    const [scale, setScale] = useState('linear');
    const [expanded, setExpanded] = useState({});
    const [getLogsDisabled, setGetLogsDisabled] = useState(false);
    const [uuid, setUUID] = useState();
    const { systemHealth, sensorNames } = useContext(DataContext);
    const systemHealthPerms = Auth.permissions.get('SystemHealth');

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    const gauges = [
        {
            title: 'CPU',
            val: () => get(health, (MC2CHECK ? '' : '') + 'health_metrics.incr_cpuinfo_pct.total.used') || 0
        },
        {
            title: 'RAM',
            val: () => {
                const avail = get(health, 'health_metrics.system.meminfo.avail_memory') || 0;
                const total = get(health, 'health_metrics.system.meminfo.total_memory') || 1;
                return 100 * (1.0 - avail / total);
            }
        },
        {
            title: 'Disk',
            val: () => {
                const disk = (get(health, 'health_metrics.disks') || [])
                    .find(d => d.mount_point === '/') || { utilization: 0 };
                return 100 * disk.utilization;
            },
            severities: { green: 92, yellow: 95, red: 999 }
        },
        {
            title: 'Inodes',
            val: () => {
                const disk = (get(health, 'health_metrics.disks') || [])
                    .find(d => d.mount_point === '/') || { inode_utilization: 0 };
                return 100 * disk.inode_utilization;
            },
        },
    ];

    const handleChangeScale = (e, v) => setScale(v);

    const handleExpandPanel = (key) => (event, isExpanded) => {
        setExpanded({ ...expanded, [key]: isExpanded });
    };

    useEffect(() => {
        // read user pofile
        const profile = getProfile('cmchealth');
        setScale(profile.scale || 'linear');
        setExpanded(profile.details || {});
    }, []);

    useEffect(() => {
        if (systemHealth) {
            if (MC2CHECK) {
                HealthService.getSystemHealth(params.uuid).then(res => {
                    setHealth(res);
                });
            } else {
                setHealth((systemHealth || {}).object);
            }
            const uuid = params.uuid || get(systemHealth, 'object.health_metrics.device_uuid', get(systemHealth, 'health_metrics.device_uuid', ''));
            setUUID(uuid);
            uuid && HealthService.getHealthHistory(uuid).then((data) => {
                setHistory(data);
            }).catch(err => {
                console.error(err);
                setHealth({});
            });

            setHistoryMetrics(HealthService.getHistoryMetrics(sensorNames));
        }
    }, [systemHealth, MC2CHECK, params.uuid, sensorNames]);

    const isVM = () => {
        return IsVM(
            get(health, 'health_metrics.model'),
            get(health, 'health_metrics.system.machine_type'));
    };

    // stash profile hook
    useEffect(() => {
        updateProfile('cmchealth', { details: expanded, scale });
    }, [scale, expanded]);

    const handleGetLogs = () => {
        toast.info('Please wait while the logs are gathered. This may take awhile...');
        setGetLogsDisabled(true);

        fetch(MC2CHECK
            ? `/api/mc2/cmc/${params.uuid}/health/logdump/?t=${Auth.getAuth().token}`
            : '/api/system/logdump', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${Auth.getAuth().token}`
            },
        }).then(response => {
            if (response.status === 200) {
                let filename = response.headers.get('content-disposition') || `${params.uuid}.logs.tar.gz`;
                if (filename.split(';').length > 1) {
                    filename = filename.split(';')[1];
                    if (filename.split('filename').length > 1) {
                        filename = filename.split('filename')[1];
                        if (filename.split('=').length > 1) {
                            filename = filename.split('=')[1];
                            if (filename.split('"').length > 1) {
                                filename = filename.split('"')[1].trim();
                            }
                        }
                    }
                }
                response.arrayBuffer().then(buffer => {
                    const blob = new Blob([buffer], { type: 'application/x-gzip' });
                    if (window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(blob, filename);
                    } else {
                        const a = document.createElement('a');
                        a.style = 'display: none';
                        document.body.appendChild(a);
                        a.href = window.URL.createObjectURL(blob);
                        a.download = filename;
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(blob);
                    }
                }, err => {
                    console.error(err);
                });
            } else {
                setGetLogsDisabled(false);
                toast.error('Unable to retrieve CMC logs...');
            }
            setGetLogsDisabled(false);
        });
    };

    const getOldestAlert = (timestamp) => {
        if (timestamp === 'N/A') {
            return 'N/A';
        }
        return Moment(timestamp).format(theme.date.longtz);
    }

    return (
        <React.Fragment>
            {(get(systemHealth, 'object.health_metrics.issues', []) || []).map((issue, i) =>
                <Box key={`${issue.id}_${i}`}
                    className={classes.errorPanel}>
                    {issue.message}
                </Box>
            )}
            <Box mb={3} display="flex" justifyContent="flex-end">
                {systemHealthPerms.write && <Box alignSelf="flex-end"><Button
                    variant="contained"
                    aria-label="get logs"
                    color="primary"
                    onClick={handleGetLogs}
                    disabled={getLogsDisabled}
                >Get Logs</Button></Box>}
            </Box>
            <Box className={classes.panel}>
                <Grid container className={classes.healthGrid} spacing={4}>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>Model</Typography>
                        <Typography className={classes.value}>
                            {get(health, 'health_metrics.model')}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>Version</Typography>
                        <Typography className={classes.value}>
                            {get(health, 'health_metrics.version')}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>License Valid Through</Typography>
                        <Typography className={classes.value}>
                            {Moment(get(health, 'health_metrics.license.expiry'), 'MMM DD, YYYY')
                                .format(theme.date.short)}
                        </Typography>
                    </Grid>
                    {get(health, 'instance_id') &&
                        <Grid item xs={2}>
                            <Typography className={classes.key}>AWS Instance ID</Typography>
                            <Typography className={classes.value}>
                                {get(health, 'instance_id')}
                            </Typography>
                        </Grid>
                    }
                    {get(health, 'avail_zone') &&
                        <Grid item xs={2}>
                            <Typography className={classes.key}>AWS Avail. Zone</Typography>
                            <Typography className={classes.value}>
                                {get(health, 'avail_zone')}
                            </Typography>
                        </Grid>
                    }
                    <Grid item xs={2}>
                        <Typography className={classes.key}>UUID</Typography>
                        <Typography className={classes.value}>
                            {get(health, 'health_metrics.device_uuid')}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>Boot Time</Typography>
                        <Typography className={classes.value}>
                            {Moment(get(health, 'health_metrics.system.boot_time')).format(theme.date.longtz)}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>MAC Address</Typography>
                        <Typography className={classes.value}>
                            {get(health, 'health_metrics.system.mac_mgmnt') || 'Unknown'}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>Machine Type</Typography>
                        <Typography className={classes.value}>
                            {get(health, 'health_metrics.system.machine_type')}
                        </Typography>
                    </Grid>
                    {!isVM() &&
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Serial Number</Typography>
                            <Typography className={classes.value}>
                                {get(health, 'health_metrics.system.serialno') || 'Unknown'}
                            </Typography>
                        </Grid>
                    }
                    <Grid item xs={2}>
                        <Typography className={classes.key}>Cores</Typography>
                        <Typography className={classes.value}>
                            {get(health, 'health_metrics.system.cpuinfo.total.num_cores')}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>RAM</Typography>
                        <Typography className={classes.value}>
                            {Bytes(get(health, 'health_metrics.system.meminfo.total_memory') * 1024)}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography className={classes.key}>Oldest Alert</Typography>
                        <Typography className={classes.value}>
                            {getOldestAlert(get(health, 'health_metrics.oldest_alert', 'N/A'))}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider className={classes.divider}/>

                <Grid container justifyContent="space-evenly" style={{ marginBottom: theme.spacing(3) }}>
                    {gauges.map((v, i) => {
                        return (<MultiGauge
                            label={v.title}
                            value={v.val()}
                            key={`${v.title}-${i}`}
                            data={v.data}
                            severities={v.severities}
                            small={v.small}
                            size={v.size}
                            colors={v.colors}/>);
                    })}
                </Grid>

                <Grid container spacing={2} direction="column" alignItems="flex-end">
                    <Grid item>
                        <ToggleButtonGroup size="small" value={scale} exclusive onChange={handleChangeScale}>
                            <ToggleButton aria-label="log scale" value="log">
                                Logarithmic Scale
                            </ToggleButton>
                            <ToggleButton aria-label="linear scale" value="linear">
                                Linear Scale
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>

                {historyMetrics.map(h => {
                    if ((h.name === 'aws_stats' && Boolean((health || {}).instance_id)) || h.name !== 'aws_stats') {
                        return (
                            <HealthPanel
                                key={h.name}
                                info={h}
                                uuid={uuid}
                                type={'cmc'}
                                data={history}
                                scale={scale}
                                expanded={!!expanded[h.name]}
                                onExpand={handleExpandPanel(h.name)}
                            />
                        );
                    } else return null;
                })}

            </Box>

        </React.Fragment>
    );
};

export default CMCHealth;
